<template>
  <div id="app">
    <!-- 頁首 (Header) -->
    <header class="header">
      <div class="container">
        <div class="header-content">
          <!-- LOGO 區塊 -->
          <div class="logo-section">
            <div class="logo" @click="goToHome">
              <img src="/images/cge_logo2.png" alt="通識中心" />
            </div>
            
            <!-- 活動總覽下拉選單（在 LOGO 右側） -->
            <div class="dropdown activity-dropdown" @click.stop>
              <button class="dropdown-button" @click="toggleActivityDropdown">
                活動總覽 ▼
              </button>
              <div class="dropdown-menu" v-if="showActivityDropdown">
                <router-link to="/search" class="dropdown-item" @click="closeDropdowns">
                  活動查詢
                </router-link>
                <router-link
                  v-if="isLoggedIn && (user.role === 'admin' || user.is_authorized === 1)"
                  to="/create-activity"
                  class="dropdown-item"
                  @click="closeDropdowns"
                >
                  活動申請
                </router-link>
              </div>
            </div>
          </div>

          <!-- 右側導航區 -->
          <div class="nav-section">
            <!-- 桌面版導航 -->
            <div class="desktop-nav" v-if="!isMobile">
              <!-- 已登入：展開顯示（取消下拉），兩行、靠右對齊 -->
              <div v-if="isLoggedIn" class="user-nav">
                <div class="user-nav-row">
                  <router-link v-if="user.role === 'admin'" to="/activity-approval" class="nav-link">活動審核</router-link>
                  <router-link v-if="user.role === 'admin' || user.is_authorized === 1" to="/registration-management" class="nav-link">活動管理</router-link>
                  <span class="user-name-label">使用者：{{ user.name }}</span>
                </div>
                <div class="user-nav-row">
                  <router-link to="/my-activities" class="nav-link">我的活動</router-link>
                  <router-link to="/essay-submission" class="nav-link">心得繳交</router-link>
                  <router-link to="/profile" class="nav-link">個人資訊</router-link>
                  <a href="#" @click.prevent="logout" class="nav-link logout-link">登出</a>
                </div>
              </div>

              <!-- 未登入時顯示登入按鈕 -->
              <router-link v-else to="/login" class="login-btn">
                登入
              </router-link>
            </div>

            <!-- 手機版導航 -->
            <div class="mobile-nav" v-else>
              <div class="menu-toggle" @click="toggleMobileMenu">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div class="mobile-menu" :class="{ active: showMobileMenu }">
                <router-link to="/search" @click="closeMobileMenu">活動查詢</router-link>
                <router-link
                  v-if="isLoggedIn && (user.role === 'admin' || user.is_authorized === 1)"
                  to="/create-activity"
                  @click="closeMobileMenu"
                >活動申請</router-link>
                
                <template v-if="isLoggedIn">
                  <router-link to="/my-activities" @click="closeMobileMenu">我的活動</router-link>
                  <router-link to="/essay-submission" @click="closeMobileMenu">心得繳交</router-link>
                  <router-link to="/profile" @click="closeMobileMenu">個人資訊</router-link>
                  
                  <router-link v-if="user.role === 'admin'" to="/activity-approval" @click="closeMobileMenu">活動審核</router-link>
                  <router-link v-if="user.role === 'admin' || user.is_authorized === 1" to="/registration-management" @click="closeMobileMenu">活動管理</router-link>
                  
                  <a href="#" @click.prevent="logout">登出</a>
                </template>
                
                <router-link v-else to="/login" @click="closeMobileMenu">登入</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- 主內容區 -->
    <main class="main-content">
      <router-view v-slot="{ Component }">
        <component :is="Component" @login-success="handleLoginSuccess" />
      </router-view>
    </main>
  </div>
</template>

<script>
import { getProfile, refreshToken } from '@/api.js'

export default {
  name: 'App',
  data() {
    return {
      isMobile: window.innerWidth < 768,
      showMobileMenu: false,
      showActivityDropdown: false,
      showUserDropdown: false,
      user: {
        user_id: '',
        name: '',
        role: '',
        is_authorized: 0
      },
      isLoggedIn: false,
      tokenRefreshTimer: null
    }
  },
  created() {
    this.fetchUserProfile()
    this.startTokenRefresh()

    // 監聽視窗縮放
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768
    })

    // 點擊外部關閉下拉選單
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
    this.stopTokenRefresh()
  },
  methods: {
    async fetchUserProfile() {
      const token = localStorage.getItem('access_token')
      if (!token) {
        this.isLoggedIn = false
        this.user = { user_id: '', name: '', role: '', is_authorized: 0 }
        localStorage.removeItem('user_info')
        return
      }

      try {
        const res = await getProfile()
        this.user = {
          user_id: res.data.user_id,
          name: res.data.name,
          role: res.data.role,
          is_authorized: res.data.is_authorized || 0
        }
        this.isLoggedIn = true
        localStorage.setItem('user_info', JSON.stringify(res.data))
      } catch (err) {
        console.error('fetchUserProfile 錯誤：', err)
        localStorage.removeItem('access_token')
        localStorage.removeItem('token_type')
        localStorage.removeItem('user_info')
        this.isLoggedIn = false
        this.user = { user_id: '', name: '', role: '', is_authorized: 0 }
      }
    },

    async handleLoginSuccess() {
      await this.fetchUserProfile()
    },

    goToHome() {
      this.$router.push('/')
    },

    toggleActivityDropdown() {
      this.showActivityDropdown = !this.showActivityDropdown
      this.showUserDropdown = false
    },

    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown
      this.showActivityDropdown = false
    },

    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
    },

    closeMobileMenu() {
      this.showMobileMenu = false
    },

    closeDropdowns() {
      this.showActivityDropdown = false
      this.showUserDropdown = false
    },

    handleOutsideClick(e) {
      // 如果點擊不在下拉選單內，關閉所有下拉選單
      if (!e.target.closest('.dropdown')) {
        this.closeDropdowns()
      }
    },

    logout() {
      this.stopTokenRefresh()
      localStorage.removeItem('access_token')
      localStorage.removeItem('token_type')
      localStorage.removeItem('user_info')
      this.user = { user_id: '', name: '', role: '', is_authorized: 0 }
      this.isLoggedIn = false
      this.showMobileMenu = false
      this.showUserDropdown = false
      this.$router.push({ name: 'Login' })
    },

    // 只要頁面開著且已登入，每 30 分鐘自動續期一次 token，
    // 讓活動報到等長時間停留的頁面不會因 token 到期被登出。
    startTokenRefresh() {
      if (this.tokenRefreshTimer) return
      this.tokenRefreshTimer = setInterval(async () => {
        if (!localStorage.getItem('access_token')) return
        try {
          const res = await refreshToken()
          if (res.data && res.data.access_token) {
            localStorage.setItem('access_token', res.data.access_token)
          }
        } catch (e) {
          // 續期失敗（例如 token 已失效）時不強制動作，交由後續請求的 401 攔截器處理
          console.warn('token 續期失敗:', e?.response?.status || e?.message)
        }
      }, 30 * 60 * 1000)
    },

    stopTokenRefresh() {
      if (this.tokenRefreshTimer) {
        clearInterval(this.tokenRefreshTimer)
        this.tokenRefreshTimer = null
      }
    }
  }
}
</script>

<style scoped>
/* 容器 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
.header {
  background-color: #fff;
  border-bottom: 2px solid #ddd;
  padding: 10px 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 70px;
}

/* Logo 區域 */
.logo-section {
  display: flex;
  align-items: center;
  gap: 30px;
}

.logo {
  cursor: pointer;
  transition: opacity 0.3s;
}

.logo:hover {
  opacity: 0.8;
}

.logo img {
  height: 80px;  /* 放大 LOGO */
  object-fit: contain;
}

/* 導航區 */
.nav-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* 桌面版導航 */
.desktop-nav {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* 下拉選單 */
.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-button {
  background: white;
  border: 1px solid #ddd;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dropdown-button:hover {
  background-color: #f0f0f0;
  border-color: #007bff;
  color: #007bff;
}

.user-button {
  color: #007bff;
  font-weight: 600;
  border-color: #007bff;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 200px;
  z-index: 1000;
  overflow: hidden;
}

.user-menu {
  right: 0;
  left: auto;
}

.dropdown-item {
  display: block;
  padding: 14px 20px;
  color: #333;
  text-decoration: none;
  transition: all 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  color: #007bff;
  padding-left: 24px;
}

.logout-item {
  color: #dc3545;
}

.logout-item:hover {
  background-color: #fff5f5;
  color: #dc3545;
}

/* 登入按鈕 */
.login-btn {
  padding: 10px 24px;
  background-color: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0,123,255,0.2);
}

.login-btn:hover {
  background-color: #0056b3;
  box-shadow: 0 4px 8px rgba(0,123,255,0.3);
  transform: translateY(-1px);
}

/* 管理功能 */
.admin-nav {
  display: flex;
  gap: 15px;
  padding-right: 20px;
  border-right: 1px solid #ddd;
}

/* 使用者導航（展開式，兩行靠右） */
.user-nav {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.user-nav-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.user-name-label {
  font-weight: 600;
  color: #007bff;
  font-size: 14px;
}

.logout-link {
  color: #dc3545;
}

.logout-link:hover {
  color: #dc3545;
  background-color: #fff5f5;
}

.nav-link {
  color: #666;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.3s;
  font-size: 14px;
  font-weight: 500;
}

.nav-link:hover {
  color: #007bff;
  background-color: #f0f7ff;
}

/* 手機版導航 */
.mobile-nav {
  position: relative;
}

.menu-toggle {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  width: 30px;
  height: 20px;
  justify-content: space-between;
}

.menu-toggle span {
  display: block;
  height: 3px;
  background-color: #333;
  transition: 0.3s;
  border-radius: 2px;
}

.mobile-menu {
  display: none;
  position: absolute;
  top: 40px;
  right: 0;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
}

.mobile-menu.active {
  display: block;
}

.mobile-menu a {
  display: block;
  padding: 14px 20px;
  color: #333;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s;
}

.mobile-menu a:last-child {
  border-bottom: none;
}

.mobile-menu a:hover {
  background-color: #f8f9fa;
  color: #007bff;
  padding-left: 24px;
}

/* 主內容區域 */
.main-content {
  min-height: calc(100vh - 100px);
  padding: 20px;
  background-color: #f5f5f5;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .container {
    padding: 0 15px;
  }
  
  .header-content {
    min-height: 60px;
  }
  
  .logo img {
    height: 60px;
  }
  
  .logo-section {
    gap: 15px;
  }
  
  .desktop-nav {
    display: none;
  }
  
  .activity-dropdown .dropdown-menu {
    right: auto;
    left: 0;
    min-width: 150px;
  }
  
  .dropdown-button {
    padding: 8px 12px;
    font-size: 14px;
  }
}
</style>